<script lang="ts" setup>
import { ref, computed, Ref, inject, onBeforeMount, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useEvents } from '@/plugins/events';
import { useLocalization } from '@/plugins/localization';
import { KeyValue, KeyValuePair } from '@/helpers/Interfaces';
import { FormEntry } from '../form';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { UserGroupEntry, UserGroupType, instanceOfUserGroupEntry, UserGroupTypes, UserGroupFilterOptions, KeyValueWithType } from '.';
import properties from '../../properties';
import { useRoute } from 'vue-router';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import TeamsService from '@/modules/core/teams/services/TeamsService';
import DataSourcesService from '@/modules/studio/data-sources/services/DataSourcesService';
import { useDynamicTrigger, useDynamicTriggerChannelId } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'user-group-blueprint',
    components: {
        ...properties
    }
});

const props = defineProps({
  "form": null,
  "parent": null,
  "blueprint": null,
  "entry": null,
  "index": null
});

const getDynamicTriggerChannelId = useDynamicTriggerChannelId(() => props.form.blueprint.id);

const { $events } = useEvents();
const { $t } = useLocalization();
const route = useRoute();
const blueprint = computed(() => props.blueprint);
const level = inject<number>('level', 0);

const selectedTeams = ref<KeyValuePair[]>([]);
const selectedProcessGroups = ref<KeyValuePair[]>([]);
const selectedUnits = ref<KeyValuePair[]>([]);
const entryData = ref(new UserGroupEntry()) as Ref<UserGroupEntry>;
const isExternalForm = computed(() => route.meta.externalAccess === true);

const triggerComponentCtx = createTriggerComponentCtx(
    {

    }
);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfUserGroupEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);

        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();

        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const value = computed({
    get()
    {
        if (!props.blueprint.multiselect)
        {
            return entryData.value.data?.[0] ?? null;
        }

        return entryData.value.data ?? null;
    },
    set(value: KeyValueWithType | KeyValueWithType[])
    {
        if (!props.blueprint.multiselect && !Array.isArray(value))
        {
            entryData.value.data = value ? [value] : null;
        }
        else if (props.blueprint.multiselect && Array.isArray(value))
        {
            entryData.value.data = value;
        }
        else
        {
            entryData.value.data = null;
        }

        useDynamicTrigger($events, getDynamicTriggerChannelId()).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
    }
});

const type = computed({
    get()
    {
        return entryData.value.settings;
    },
    set(value: KeyValue<UserGroupTypes, string>)
    {
        entryData.value.settings = value;
    }
});

const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));
const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const placeholder = computed(() => props.form.localization.translate(props.blueprint.placeholder));
const design = computed(() => props.form.designMode());
const endpoint = computed(() =>
{
    if (design.value) return null;

    if (props.entry.fieldIndex)
    {
        return `modules/${props.entry.moduleId}/table-templates/select-options/${props.entry.fieldIndex}/${props.blueprint.name}`;
    }

    if (isExternalForm.value)
    {
        return `external-form/${props.entry.module}/${props.entry.formId}/select-options/${props.entry.actionName}/${props.blueprint.name}`;
    }

    return `simple-dictionary/${props.entry.module}/form/${props.entry.formId}/${props.entry.actionName}/${props.blueprint.name}`;
});

const preselect = computed(() =>
{
    if (!design.value)
    {
        if (Array.isArray(value.value) && value.value.length === 0) return true;

        if (!Array.isArray(value.value) && !value.value) return true;
    }

    return false;
});

const typeOptions = computed(() =>
{
    return [
        {
            key: UserGroupTypes.Team,
            value: $t('[[[Zespół]]]'),
        },
        {
            key: UserGroupTypes.ProcessGroup,
            value: $t('[[[Grupy użytkowników]]]'),
        },
        {
            key: UserGroupTypes.Unit,
            value: $t('[[[Dział]]]'),
        },
    ];
});

const userGroupFilterOptions = computed(() =>
{
    return [
        {
            value: UserGroupFilterOptions.ShowAll,
            text: '[[[Wyświetlaj wszystkie]]]'
        },
        {
            value: UserGroupFilterOptions.ShowSelected,
            text: '[[[Wyświetlaj wybrane]]]'
        },
        {
            value: UserGroupFilterOptions.DoNotShow,
            text: '[[[Nie wyświetlaj]]]'
        }
    ];
});

const allowedTypeOptions = computed(() =>
{
    const result: KeyValue<number, string>[] = [];

    if (props.blueprint.teamsFilter !== UserGroupFilterOptions.DoNotShow)
        result.push(typeOptions.value[0]);

    if (props.blueprint.processGroupsFilter !== UserGroupFilterOptions.DoNotShow)
        result.push(typeOptions.value[1]);

    if (props.blueprint.unitsFilter !== UserGroupFilterOptions.DoNotShow)
        result.push(typeOptions.value[2]);

    return result;
});

const updateSelectedTeamsValue = (): void =>
{
    blueprint.value.selectedTeams = selectedTeams.value.map((p) => p.key);
};

const updateSelectedProcessGroupsValue = (): void =>
{
    blueprint.value.selectedProcessGroups = selectedProcessGroups.value.map((p) => p.key);
};

const updateSelectedUnitsValue = (): void =>
{
    blueprint.value.selectedUnits = selectedUnits.value.map((p) => p.key);
};

const fetchSelectedTeams = async (): Promise<void> =>
{
    if (blueprint.value.selectedTeams?.length)
    {
        const result = await DataSourcesService.fetchUserFilterGroups(props.form.blueprintId, "Team" , blueprint.value.selectedTeams);

        const filteredResult = result.items.filter(p => blueprint.value.selectedTeams.includes(p.result.key)).select(x => x.result).toArray();

        selectedTeams.value = filteredResult;
    }
};

const fetchSelectedProcessGroups = async (): Promise<void> =>
{
    if (blueprint.value.selectedProcessGroups?.length)
    {
        const result = await DataSourcesService.fetchUserFilterGroups(props.form.blueprintId, "Group" , blueprint.value.selectedProcessGroups);

        const filteredResult = result.items.filter(p => blueprint.value.selectedProcessGroups.includes(p.result.key)).select(x => x.result).toArray();

        selectedProcessGroups.value = filteredResult;
    }
};

const fetchSelectedUnits = async (): Promise<void> =>
{
    if (blueprint.value.selectedUnits?.length)
    {
        const result = await DataSourcesService.fetchUserFilterGroups(props.form.blueprintId, "Unit" , blueprint.value.selectedUnits);

        const filteredResult = result.items.filter(p => blueprint.value.selectedUnits.includes(p.result.key)).select(x => x.result).toArray();

        selectedUnits.value = filteredResult;
    }
};

const clearInputs = () =>
{
    value.value = null;
    type.value = allowedTypeOptions.value?.length ? allowedTypeOptions.value[0] : null;
};

const clearSelectedProcessGroups = () =>
{
    selectedProcessGroups.value = [];
    blueprint.value.selectedProcessGroups = [];

    if (blueprint.value.processGroupsFilter === UserGroupFilterOptions.ShowAll || blueprint.value.processGroupsFilter === UserGroupFilterOptions.ShowSelected)
    {
        blueprint.value.groupTypes[getGroupTypeStringKey(typeOptions.value[1].key)] = true;
    }
    else
    {
        blueprint.value.groupTypes[getGroupTypeStringKey(typeOptions.value[1].key)] = false;
    }
};

const clearSelectedTeams = () =>
{
    selectedTeams.value = [];
    blueprint.value.selectedTeams = [];

    if (blueprint.value.teamsFilter === UserGroupFilterOptions.ShowAll || blueprint.value.teamsFilter === UserGroupFilterOptions.ShowSelected)
        blueprint.value.groupTypes[getGroupTypeStringKey(typeOptions.value[0].key)] = true;
    else
        blueprint.value.groupTypes[getGroupTypeStringKey(typeOptions.value[0].key)] = false;

};

const clearSelectedUnits = () =>
{
    selectedUnits.value = [];
    blueprint.value.selectedUnits = [];

    if (blueprint.value.unitsFilter === UserGroupFilterOptions.ShowAll || blueprint.value.unitsFilter === UserGroupFilterOptions.ShowSelected)
        blueprint.value.groupTypes[getGroupTypeStringKey(typeOptions.value[2].key)] = true;
    else
        blueprint.value.groupTypes[getGroupTypeStringKey(typeOptions.value[2].key)] = false;
};

const getGroupTypeStringKey = (key: number): string => UserGroupTypes[key];

const setSettings = () =>
{
    const _type = Array.isArray(value.value) ? value.value[0]?.type : value.value?.type;

    type.value = typeOptions.value.find((p) => p.key === _type) ?? null;
};

!preselect.value && setSettings();

if (design.value)
{
    fetchSelectedTeams();

    if (props.form.blueprintId != 0)
    {
        fetchSelectedProcessGroups();
    }

    fetchSelectedUnits();
}
</script>

<template>
    <form-component-wrapper class="userGroup-component" :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <div class="row">
                    <ideo-select
                        v-model="value"
                        :id="blueprint.id"
                        :multiselect="blueprint.multiselect"
                        :disabled="readonly"
                        :filters="type?.key ? { [`${blueprint.name}_type`]: type.key } : null"
                        :placeholder="placeholder"
                        :endpoint="endpoint"
                        :clear-button="false"
                        class="col-sm-6 pe-sm-0"
                        custom-classes="border-radius-right-sm-none"
                        :data-test="blueprint.id"
                    >
                        <template #option="{ option }">
                            <user-avatar
                                :src="$filters.baseurl(`storage/files/${option.pictureUrl}`)"
                                :username="option.value"
                                rounded="circle"
                            />
                            <span class="ms-2 me-1">{{ option.value }}</span>
                            <span v-if="option.unit">({{ option.unit }})</span>
                        </template>
                    </ideo-select>
                    <div class="d-flex col-sm-6 ps-sm-0 mt-1 mt-sm-0">
                        <ideo-select
                            v-model="type"
                            :placeholder="$t('[[[Typ]]]')"
                            :disabled="readonly"
                            :deselect="false"
                            :clear-button="false"
                            :preselect-first-option="preselect"
                            :options="allowedTypeOptions"
                            class="flex-fill"
                            custom-classes="border-radius-right-none border-radius-left-sm-none"
                            @update:modelValue="value = null"
                        />
                        <div class="input-group-append h-100">
                            <button
                                type="button"
                                class="btn btn-light border-radius-left-none h-100"
                                @click.stop="clearInputs"
                            >
                                <i class="fas fa-fw fa-xmark"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-column">
                    <form-help :form="form" :blueprint="blueprint" />
                    <form-error-message :entry="entryData" name="value" />
                    <form-error-message :entry="entryData" name="custom" />
                </div>
            </div>
        </template>

        <template #properties>
            <field-name v-model="blueprint.name" :form="form" :blueprint="blueprint" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-checkbox
                        v-model="blueprint.multiselect"
                        :label="$t('[[[Wielokrotny wybór]]]')"
                        :invalid-feedback="() => form.schema.errorMessage(blueprint, 'multiselect')"
                    />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Tekst zastępczy]]]')" />
                    </ideo-form-localize>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" :label="$t('[[[Pole wymagane]]]')" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Filtry]]]')">
                    <field-choice v-model="blueprint.teamsFilter" :options="userGroupFilterOptions" :label="label || $t('[[[Zespoły]]]')" @update:modelValue="clearSelectedTeams" />
                    <ideo-form-group v-if="blueprint.teamsFilter == UserGroupFilterOptions.ShowSelected" :label="$t('[[[Wybierz zespoły]]]')" :invalid-feedback="$t(form.schema.errorMessage(blueprint, 'teamsFilter'))">
                        <ideo-select
                            v-model="selectedTeams"
                            :endpoint="`admin/forms/${form.blueprintId}/user-filter-groups/Team`"
                            :multiselect="true"
                            @update:modelValue="updateSelectedTeamsValue"
                        />
                    </ideo-form-group>
                    <field-choice v-model="blueprint.processGroupsFilter" :options="userGroupFilterOptions" :label="label || $t('[[[Grupy użytkowników]]]')" @update:modelValue="clearSelectedProcessGroups" />
                    <ideo-form-group v-if="blueprint.processGroupsFilter == UserGroupFilterOptions.ShowSelected" :label="$t('[[[Wybierz grupy użytkowników]]]')" :invalid-feedback="$t(form.schema.errorMessage(blueprint, 'processGroupsFilter'))">
                        <ideo-select
                            v-model="selectedProcessGroups"
                            :endpoint="`admin/forms/${form.blueprintId}/user-filter-groups/Group`"
                            :multiselect="true"
                            @update:modelValue="updateSelectedProcessGroupsValue"
                        />
                    </ideo-form-group>
                    <field-choice v-model="blueprint.unitsFilter" :options="userGroupFilterOptions" :label="label || $t('[[[Działy]]]')" @update:modelValue="clearSelectedUnits" />
                    <ideo-form-group v-if="blueprint.unitsFilter == UserGroupFilterOptions.ShowSelected" :label="$t('[[[Wybierz działy]]]')" :invalid-feedback="$t(form.schema.errorMessage(blueprint, 'unitsFilter'))">
                        <ideo-select
                            v-model="selectedUnits"
                            :endpoint="`admin/forms/${form.blueprintId}/user-filter-groups/Unit`"
                            :multiselect="true"
                            @update:modelValue="updateSelectedUnitsValue"
                        />
                    </ideo-form-group>
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
